import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LayoutDashboard, LogOut, Search, ArrowLeft, Plus, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Application = {
  id: string;
  created_at: string | null;
  email: string;
  display_name: string | null;
  phone: string | null;
  company: string | null;
  status: string;
  flagged_abuse?: boolean;
  is_subscribed?: boolean;
  trial_requests_used?: number;
  trial_start_date?: string | null;
  linked_accounts_count?: number;
};

type ComputedStatus = 'pending' | 'trial' | 'trial_ended' | 'active' | 'blocked';

const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "default",
  trial: "outline",
  trial_ended: "destructive",
  active: "secondary",
  blocked: "destructive",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  trial: "Trial",
  trial_ended: "Trial Ended",
  active: "Active",
  blocked: "Blocked",
  flagged: "Flagged",
};

function computeEffectiveStatus(app: Application): ComputedStatus {
  if (app.status === 'pending') return 'pending';
  if (app.status === 'blocked') return 'blocked';
  if (app.status === 'active') {
    if (app.is_subscribed) return 'active';
    const requestsUsed = app.trial_requests_used ?? 0;
    const trialStart = app.trial_start_date ? new Date(app.trial_start_date) : null;
    const daysElapsed = trialStart
      ? Math.floor((Date.now() - trialStart.getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    if (requestsUsed >= 10 || daysElapsed >= 7) return 'trial_ended';
    return 'trial';
  }
  return app.status as ComputedStatus;
}

const AdminMembershipApplications = () => {
  const { signOut, user } = useAuth();
  const { toast } = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [newStatus, setNewStatus] = useState("active");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    const { data: apps, error } = await supabase
      .from("membership_applications")
      .select("id, created_at, email, display_name, phone, company, status, flagged_abuse")
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load applications.", variant: "destructive" });
      setLoading(false);
      return;
    }

    const enrichedApps = (apps || []).map((app) => app as Application);
    setApplications(enrichedApps);
    setLoading(false);
    enrichWithProfileData(enrichedApps);
  };

  const enrichWithProfileData = async (apps: Application[]) => {
    try {
      const { data, error } = await supabase.functions.invoke('get-member-statuses');
      if (!error && data?.members) {
        const memberMap = new Map(
          (data.members as Array<{
            email: string;
            is_subscribed: boolean;
            trial_requests_used: number;
            trial_start_date: string | null;
            flagged_abuse: boolean;
            linked_accounts_count: number;
          }>).map((m) => [m.email, m])
        );
        setApplications(apps.map(app => {
          const memberData = memberMap.get(app.email);
          if (memberData) {
            return {
              ...app,
              is_subscribed: memberData.is_subscribed,
              trial_requests_used: memberData.trial_requests_used,
              trial_start_date: memberData.trial_start_date,
              flagged_abuse: memberData.flagged_abuse,
              linked_accounts_count: memberData.linked_accounts_count,
            };
          }
          return app;
        }));
      }
    } catch (e) {
      console.warn('Could not fetch enriched member data:', e);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    const { error } = await supabase
      .from("membership_applications")
      .update({ status: newStatus })
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Failed to update status.", variant: "destructive" });
    } else {
      setApplications((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
      );
      toast({ title: "Updated", description: `Status set to ${newStatus}.` });
    }
  };

  const resetForm = () => {
    setNewEmail("");
    setNewName("");
    setNewPhone("");
    setNewCompany("");
    setNewStatus("active");
  };

  const handleAddMember = async () => {
    const trimmedEmail = newEmail.trim().toLowerCase();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("membership_applications").insert({
      email: trimmedEmail,
      display_name: newName.trim() || null,
      phone: newPhone.trim() || null,
      company: newCompany.trim() || null,
      status: newStatus,
    });
    if (error) {
      toast({ title: "Error", description: error.message || "Failed to add member.", variant: "destructive" });
    } else {
      toast({ title: "Member added", description: `${trimmedEmail} added as ${newStatus}.` });
      resetForm();
      setDialogOpen(false);
      fetchApplications();
    }
    setSubmitting(false);
  };

  const filtered = applications.filter((a) => {
    const computedStatus = computeEffectiveStatus(a);
    const matchesSearch = !search || a.email.toLowerCase().includes(search.toLowerCase());
    if (statusFilter === "flagged") return matchesSearch && a.flagged_abuse;
    const matchesStatus = statusFilter === "all" || computedStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = applications.reduce((acc, a) => {
    const s = computeEffectiveStatus(a);
    acc[s] = (acc[s] || 0) + 1;
    if (a.flagged_abuse) acc['flagged'] = (acc['flagged'] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 text-primary" />
              <h1 className="text-lg font-bold">Admin Portal</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={signOut}>
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
                </Button>
              </Link>
              <h2 className="text-xl font-bold">Membership Applications</h2>
            </div>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add Member
            </Button>
          </div>

          {/* Status filter badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(statusLabels).map(([key, label]) => (
              <Badge
                key={key}
                variant={statusFilter === key ? "default" : "outline"}
                className={`cursor-pointer ${key === 'flagged' ? 'border-orange-400 text-orange-600' : ''}`}
                onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
              >
                {key === 'flagged' && <AlertTriangle className="w-3 h-3 mr-1" />}
                {label}: {statusCounts[key] || 0}
              </Badge>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="trial_ended">Trial Ended</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="flagged">Flagged</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <p className="text-muted-foreground py-8 text-center">Loading applications...</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">No applications found.</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead className="w-[160px]">Update Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => {
                    const effectiveStatus = computeEffectiveStatus(a);
                    return (
                      <TableRow key={a.id}>
                        <TableCell className="whitespace-nowrap">
                          {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {a.email}
                            {a.flagged_abuse && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Flagged for trial abuse</p>
                                  {(a.linked_accounts_count ?? 0) > 1 && (
                                    <p className="text-xs text-muted-foreground">
                                      {a.linked_accounts_count} accounts share this device
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{a.display_name || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={statusColors[effectiveStatus] || "default"}>
                            {statusLabels[effectiveStatus] || effectiveStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {a.trial_requests_used !== undefined ? `${a.trial_requests_used}/10` : "—"}
                        </TableCell>
                        <TableCell>
                          <Select value={a.status} onValueChange={(val) => updateStatus(a.id, val)}>
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="blocked">Blocked</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </main>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="add-email">Email *</Label>
                <Input id="add-email" type="email" placeholder="user@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-name">Display Name</Label>
                <Input id="add-name" placeholder="Optional" value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="add-phone">Phone</Label>
                  <Input id="add-phone" placeholder="Optional" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="add-company">Company</Label>
                  <Input id="add-company" placeholder="Optional" value={newCompany} onChange={(e) => setNewCompany(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleAddMember} disabled={submitting}>
                {submitting ? "Adding..." : "Add Member"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default AdminMembershipApplications;
